/**
 * hreflang annotations.
 *
 * Google has no way to tell that two of our URLs are the same document in a
 * different language until we say so. Left unannotated, /legal/privacy-policy
 * and /legal/privacy-policy-cn look like two unrelated pages: a Chinese-reading
 * customer searching for our privacy policy is as likely to be shown the
 * English one, and the pages can end up competing rather than supporting each
 * other.
 *
 * This is also the piece missing before any second region can launch. A
 * Canadian variant of a legal page is not a duplicate of the Australian one,
 * and hreflang is how that distinction is expressed — an `en-CA` entry
 * alongside the `en-AU` one in the same set is the whole change.
 */

export type LanguageAlternate = {
  /**
   * A BCP-47 language tag, optionally with a script or region subtag:
   * "en", "en-AU", "zh-Hans". Google matches on language first and region
   * second, so a tag that is more specific than the content warrants wins
   * fewer searches, not more.
   */
  hreflang: string;
  /** Site-relative, matching the page's canonical exactly. */
  path: string;
  /**
   * Open Graph locale for this variant, in OG's `language_TERRITORY` form.
   * Defaults to the site's en_AU. Set it wherever the page is not in the
   * site's default language, or a translated page advertises itself to
   * social and search previews as English.
   */
  ogLocale?: string;
};

export type AlternateSet = {
  /**
   * The variant Google serves a searcher no other entry matches. Must be one
   * of `variants` — a set whose x-default points outside itself is the most
   * common way these annotations are got wrong.
   */
  xDefault: string;
  variants: readonly LanguageAlternate[];
};

/**
 * Every group of pages that say the same thing in a different language or for
 * a different region.
 *
 * A set is only worth declaring once a second variant genuinely exists and
 * carries real content. Annotating a translation that is still a placeholder
 * invites Google to swap a complete page for an empty one, which is worse than
 * saying nothing.
 */
const ALTERNATE_SETS: readonly AlternateSet[] = [
  {
    // The English policy is the fallback: it is the only one written in full,
    // and zh-Hans is a translation of it rather than a separate document.
    xDefault: "/legal/privacy-policy",
    variants: [
      { hreflang: "en-AU", path: "/legal/privacy-policy" },
      // Simplified Chinese, deliberately without a region subtag: the page
      // suits any Simplified-Chinese reader, not only one browsing from
      // Australia. Open Graph has no script subtag, so its locale is the
      // conventional zh_CN.
      { hreflang: "zh-Hans", path: "/legal/privacy-policy-cn", ogLocale: "zh_CN" },
    ],
  },
];

/** Exposed for the test that checks every annotated path is a real page. */
export const ALTERNATE_PATHS = ALTERNATE_SETS.flatMap((set) =>
  set.variants.map((v) => v.path),
);

/** Exposed so the test can check each set on its own. */
export const ALTERNATE_SETS_FOR_TEST = ALTERNATE_SETS;

/**
 * The `alternates.languages` map for a page, or undefined when the page has no
 * translations.
 *
 * Every page in a set lists every variant *including itself*. That
 * self-reference is not redundant: Google discards a set whose members do not
 * all point back at each other, so omitting it silently disables the whole
 * annotation.
 */
export function getLanguageAlternates(
  path: string,
): Record<string, string> | undefined {
  const set = ALTERNATE_SETS.find((s) => s.variants.some((v) => v.path === path));
  // A set of one describes nothing; Google's own guidance is that a
  // single-language page needs no annotation at all.
  if (!set || set.variants.length < 2) return undefined;

  const languages: Record<string, string> = {};
  for (const variant of set.variants) languages[variant.hreflang] = variant.path;
  languages["x-default"] = set.xDefault;

  return languages;
}

/**
 * The Open Graph locale for a page, or undefined to use the site default.
 *
 * Kept here rather than passed at the call site so a page's language is
 * declared exactly once, next to the hreflang tag that already states it.
 */
export function getOgLocale(path: string): string | undefined {
  for (const set of ALTERNATE_SETS) {
    const variant = set.variants.find((v) => v.path === path);
    if (variant) return variant.ogLocale;
  }
  return undefined;
}
